import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, UploadCloud, FileCheck2, X, FileUp } from "lucide-react";

const FAMILY_OPTIONS = [
  { k: "heart_disease", label: "Heart disease" },
  { k: "diabetes", label: "Type 2 diabetes" },
  { k: "stroke", label: "Stroke" },
  { k: "hypertension", label: "Hypertension" },
  { k: "cancer", label: "Cancer" },
];

export default function GenomeStart() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [mode, setMode] = useState("health_first");
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("");
  const [state, setState] = useState("");
  const [history, setHistory] = useState([]);
  const [bm, setBm] = useState({});
  const [ls, setLs] = useState({});
  const [consent, setConsent] = useState(false);

  // Genomics-first drag-drop state
  const [upload, setUpload] = useState({ file: null, status: "idle", id: null, filename: null });
  const [dragOver, setDragOver] = useState(false);

  const MAX_BYTES = 20 * 1024 * 1024;
  const ACCEPT_EXT = [".pdf", ".vcf", ".txt", ".csv", ".zip"];

  const validateFile = (f) => {
    if (!f) return "No file selected";
    if (f.size > MAX_BYTES) return "File exceeds 20 MB";
    const lower = f.name.toLowerCase();
    if (!ACCEPT_EXT.some((e) => lower.endsWith(e)))
      return `Unsupported type — accepted: ${ACCEPT_EXT.join(", ")}`;
    return null;
  };

  const uploadFile = async (f) => {
    const err = validateFile(f);
    if (err) { toast.error(err); return; }
    setUpload({ file: f, status: "uploading", id: null, filename: f.name });
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/genome/report/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUpload({ file: f, status: "done", id: data.id, filename: data.filename });
      toast.success("Report queued for counsellor review");
    } catch (e) {
      setUpload({ file: null, status: "idle", id: null, filename: null });
      toast.error(formatApiError(e));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadFile(f);
  };

  const setBmKey = (k) => (e) => setBm((p) => ({ ...p, [k]: e.target.value === "" ? undefined : Number(e.target.value) }));
  const setLsKey = (k) => (e) => setLs((p) => ({ ...p, [k]: e.target.value === "" ? undefined : Number(e.target.value) }));

  // Lab-PDF auto-parse state
  const [labParsing, setLabParsing] = useState(false);
  const [labResult, setLabResult] = useState(null); // { values, source }

  const uploadLab = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Lab reports must be PDF"); return; }
    if (f.size > 20 * 1024 * 1024) { toast.error("PDF exceeds 20 MB"); return; }
    setLabParsing(true);
    setLabResult(null);
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/genome/parse-lab", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const vals = data.values || {};
      const n = Object.keys(vals).length;
      if (n === 0) {
        toast.error("Couldn't find biomarker values in this PDF — enter manually");
      } else {
        setBm((p) => ({ ...p, ...vals }));
        setLabResult(data);
        toast.success(`Auto-filled ${n} value${n > 1 ? "s" : ""} from your lab PDF`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLabParsing(false); }
  };

  const submit = async () => {
    if (!consent) { toast.error("Please accept data-processing consent to continue"); return; }
    setSubmitting(true);
    try {
      const payload = {
        mode,
        vertical: "cardiometabolic",
        demographics: { age: age ? Number(age) : undefined, sex: sex || undefined, state: state || undefined },
        family_history: history,
        biomarkers: Object.fromEntries(Object.entries(bm).filter(([, v]) => v !== undefined)),
        lifestyle: Object.fromEntries(Object.entries(ls).filter(([, v]) => v !== undefined)),
        consent_data_processing: true,
      };
      await api.post("/genome/intake", payload);
      toast.success("Prevention map ready");
      navigate("/genome/map", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ["Mode & demographics", "Family history", "Biomarkers & lifestyle", "Consent & submit"];

  return (
    <AppShell pageTitle="GenomePramana · Start your journey" pageDescription="Four quick steps to your prevention map">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 mb-6 text-[11px] uppercase tracking-widest text-muted-foreground">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={i === step ? "text-primary font-medium" : ""}>{i + 1}. {s}</div>
              {i < steps.length - 1 && <span>·</span>}
            </React.Fragment>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-5" data-testid="genome-step-mode">
            <div>
              <Label>Starting mode</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                {["health_first", "genomics_first", "nutrition_first"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`rounded-md border p-3 text-left ${mode === m ? "border-primary bg-primary/5" : "border-border"}`}
                    data-testid={`genome-mode-${m}`}
                  >
                    <div className="font-serif text-primary text-sm">{m.replace("_", " ")}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {m === "health_first" ? "Share labs + lifestyle." : m === "genomics_first" ? "Upload your genome report later." : "Nutrition-first pathway."}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><Label>Age</Label><Input type="number" min="18" max="120" value={age} onChange={(e) => setAge(e.target.value)} data-testid="genome-age" /></div>
              <div><Label>Sex</Label>
                <select value={sex} onChange={(e) => setSex(e.target.value)} className="w-full h-10 rounded-md border border-input px-3 bg-background text-sm" data-testid="genome-sex">
                  <option value="">—</option><option value="F">Female</option><option value="M">Male</option><option value="O">Other</option>
                </select>
              </div>
              <div><Label>State</Label><Input value={state} onChange={(e) => setState(e.target.value)} placeholder="Kerala" data-testid="genome-state" /></div>
            </div>

            {mode === "genomics_first" && (
              <div data-testid="genome-upload-surface">
                <div className="flex items-center justify-between">
                  <Label>Upload your genomic report</Label>
                  <span className="text-[11px] text-muted-foreground">PDF · VCF · TXT · CSV · ZIP · ≤20 MB</span>
                </div>
                {upload.status === "done" ? (
                  <div className="mt-2 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50/60 p-4" data-testid="genome-upload-done">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileCheck2 size={18} className="text-emerald-700 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-primary truncate">{upload.filename}</div>
                        <div className="text-[11px] text-emerald-800">Queued for counsellor review · ID {upload.id}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-primary"
                      onClick={() => setUpload({ file: null, status: "idle", id: null, filename: null })}
                      data-testid="genome-upload-reset"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="genome-upload-input"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`mt-2 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                      dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/20 hover:border-primary/40"
                    }`}
                    data-testid="genome-upload-dropzone"
                  >
                    <UploadCloud size={28} className="text-primary" />
                    <div className="text-sm text-primary">
                      {upload.status === "uploading" ? "Uploading…" : "Drag & drop your report, or click to browse"}
                    </div>
                    <div className="text-[11px] text-muted-foreground max-w-md">
                      Private & encrypted. A trained counsellor will review before any insight is surfaced — we never auto-interpret genomic data.
                    </div>
                    <input
                      id="genome-upload-input"
                      type="file"
                      className="hidden"
                      accept={ACCEPT_EXT.join(",")}
                      onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                      disabled={upload.status === "uploading"}
                      data-testid="genome-upload-input"
                    />
                  </label>
                )}
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Uploads are optional — you'll still get a prevention map from the biomarkers you share next.
                </div>
              </div>
            )}

            <div className="flex justify-end"><Button onClick={() => setStep(1)} data-testid="genome-next-0">Next <ArrowRight size={14} className="ml-1" /></Button></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5" data-testid="genome-step-family">
            <div>
              <Label>Family history (first-degree)</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {FAMILY_OPTIONS.map((o) => (
                  <label key={o.k} className="flex items-center gap-2 rounded-md border border-border p-3 cursor-pointer">
                    <Checkbox
                      checked={history.includes(o.k)}
                      onCheckedChange={(c) => setHistory((h) => c ? [...h, o.k] : h.filter((x) => x !== o.k))}
                      data-testid={`genome-fh-${o.k}`}
                    />
                    <span className="text-sm">{o.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button onClick={() => setStep(2)} data-testid="genome-next-1">Next <ArrowRight size={14} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 2 && mode === "nutrition_first" && (
          <div className="space-y-5" data-testid="genome-step-nutrition">
            <div className="rounded-md border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
              <b className="text-primary">Nutrition-first doorway.</b> No lab or genome report needed — tell us your weekly eating pattern and we'll build a prevention map from your plate.
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Weekly habits — think about a typical week</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Dal / legume servings per week</Label>
                  <Input type="number" min="0" max="21" value={ls.dal_servings_week ?? ""} onChange={setLsKey("dal_servings_week")} placeholder="e.g. 5" data-testid="genome-nf-dal" />
                </div>
                <div>
                  <Label>Vegetable servings per day</Label>
                  <Input type="number" min="0" max="12" step="0.5" value={ls.veg_servings_day ?? ""} onChange={setLsKey("veg_servings_day")} placeholder="e.g. 3" data-testid="genome-nf-veg" />
                </div>
                <div>
                  <Label>Fibre servings per day</Label>
                  <Input type="number" min="0" max="12" value={ls.fiber_servings_day ?? ""} onChange={setLsKey("fiber_servings_day")} placeholder="e.g. 4" data-testid="genome-nf-fiber" />
                </div>
                <div>
                  <Label>Deep-fried meals per week</Label>
                  <Input type="number" min="0" max="21" value={ls.fried_meals_week ?? ""} onChange={setLsKey("fried_meals_week")} placeholder="e.g. 3" data-testid="genome-nf-fried" />
                </div>
                <div>
                  <Label>Sweets / sugary drinks per week</Label>
                  <Input type="number" min="0" max="30" value={ls.sugar_servings_week ?? ""} onChange={setLsKey("sugar_servings_week")} placeholder="e.g. 7" data-testid="genome-nf-sugar" />
                </div>
                <div>
                  <Label>Red meat servings per week</Label>
                  <Input type="number" min="0" max="21" value={ls.red_meat_week ?? ""} onChange={setLsKey("red_meat_week")} placeholder="e.g. 2" data-testid="genome-nf-redmeat" />
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">A couple more lifestyle signals (optional)</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><Label>Activity min/week</Label><Input type="number" value={ls.activity_mins_week ?? ""} onChange={setLsKey("activity_mins_week")} data-testid="genome-ls-activity-nf" /></div>
                <div><Label>Sleep hrs/night</Label><Input type="number" step="0.1" value={ls.sleep_hrs ?? ""} onChange={setLsKey("sleep_hrs")} data-testid="genome-ls-sleep-nf" /></div>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)} data-testid="genome-next-2">Next <ArrowRight size={14} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 2 && mode !== "nutrition_first" && (
          <div className="space-y-5" data-testid="genome-step-biomarkers">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-muted-foreground">Biomarkers (skip any you don't have — V1 scores on what we get)</div>
                <label
                  htmlFor="genome-lab-input"
                  className={`inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs text-primary hover:bg-primary/10 cursor-pointer ${labParsing ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="genome-lab-upload-btn"
                >
                  <FileUp size={13} /> {labParsing ? "Reading PDF…" : "Auto-fill from lab PDF"}
                  <input
                    id="genome-lab-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadLab(e.target.files[0])}
                    disabled={labParsing}
                    data-testid="genome-lab-input"
                  />
                </label>
              </div>
              {labResult && Object.keys(labResult.values || {}).length > 0 && (
                <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-800" data-testid="genome-lab-result">
                  Pulled {Object.keys(labResult.values).join(", ")} from your PDF
                  <span className="text-emerald-700/70"> · source: {labResult.source}</span>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label>HbA1c (%)</Label><Input type="number" step="0.1" value={bm.hba1c ?? ""} onChange={setBmKey("hba1c")} data-testid="genome-bm-hba1c" /></div>
                <div><Label>LDL (mg/dL)</Label><Input type="number" value={bm.ldl ?? ""} onChange={setBmKey("ldl")} data-testid="genome-bm-ldl" /></div>
                <div><Label>BP systolic</Label><Input type="number" value={bm.bp_sys ?? ""} onChange={setBmKey("bp_sys")} data-testid="genome-bm-bp-sys" /></div>
                <div><Label>BP diastolic</Label><Input type="number" value={bm.bp_dia ?? ""} onChange={setBmKey("bp_dia")} data-testid="genome-bm-bp-dia" /></div>
                <div><Label>BMI</Label><Input type="number" step="0.1" value={bm.bmi ?? ""} onChange={setBmKey("bmi")} data-testid="genome-bm-bmi" /></div>
                <div><Label>Waist (cm)</Label><Input type="number" value={bm.waist_cm ?? ""} onChange={setBmKey("waist_cm")} data-testid="genome-bm-waist" /></div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Lifestyle</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><Label>Activity min/week</Label><Input type="number" value={ls.activity_mins_week ?? ""} onChange={setLsKey("activity_mins_week")} data-testid="genome-ls-activity" /></div>
                <div><Label>Sleep hrs/night</Label><Input type="number" step="0.1" value={ls.sleep_hrs ?? ""} onChange={setLsKey("sleep_hrs")} data-testid="genome-ls-sleep" /></div>
                <div><Label>Fibre servings/day</Label><Input type="number" value={ls.fiber_servings_day ?? ""} onChange={setLsKey("fiber_servings_day")} data-testid="genome-ls-fiber" /></div>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)} data-testid="genome-next-2">Next <ArrowRight size={14} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="genome-step-consent">
            <div className="rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              <p>By submitting you consent to Arogya Pramana processing the biomarker + lifestyle data above to compute your prevention map. We never sell data. You can revoke consent any time from <b>Settings → Privacy</b>.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="genome-consent" />
              <span className="text-sm text-primary">I accept data-processing consent for GenomePramana (DPDP Section 6).</span>
            </label>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={submit} disabled={!consent || submitting} data-testid="genome-submit">
                {submitting ? "Scoring…" : "Build my prevention map"}
                <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
